<template>
  <DialogBase v-if="visible">
    <div class="dialog-frame dialog-frame-wide">
      <div class="dialog-frame__body onboarding-modal">
        <img src="/onboarding.png" class="onboarding-modal__image" />

        <template v-if="profileStatus === ProfileStatusEnum.INCOMPLETE_CLIENT_INPUT">
          <div class="onboarding-modal__header">Complete Your Onboarding</div>
          <template v-if="userStore.userData?.scopes?.includes('accounts.edit')">
            <div class="onboarding-modal__message">
              Welcome to Meshh Vantage!<br /><br />
              Before you can start your journey with us, we need
              you to spend a few minutes completing your profile.
            </div>

            <div class="onboarding-modal__controls">
              <Button severity="primary" @click="proceed(`/admin/profile/${userStore.userData?.account_id}`)">Proceed</Button>
            </div>
          </template>
          <template v-else>
            <div class="onboarding-modal__message">
              Welcome to Meshh Vantage!<br /><br />
              Before you can start your journey with us, a client administrator needs to complete your profile. You can see who they are by clicking the button below.
            </div>

            <div class="onboarding-modal__controls">
              <Button severity="primary" @click="proceed(`/users`)">View Users</Button>
            </div>
          </template>
        </template>
        <template v-else-if="profileStatus === ProfileStatusEnum.PENDING_REAPPROVAL">
          <div class="onboarding-modal__header">Changes Pending Approval</div>
          <div class="onboarding-modal__message">
            Thanks for making changes to your profile, this is now pending approval by a member of the Meshh team. We
            will email you once this has been approved and then you will be able to proceed with making new bookings.
            <br /><br />
            If you have any questions or need assistance, please don't hesitate to contact your Account Manager.
          </div>

          <div class="onboarding-modal__controls">
            <Button severity="primary" @click="proceed(`/overview`)">Return to Dashboard</Button>
          </div>
        </template>
        <template v-else-if="profileStatus === ProfileStatusEnum.CHANGES_REJECTED">
          <div class="onboarding-modal__header">Changes Requested</div>
          <template v-if="userStore.userData?.scopes?.includes('accounts.edit')">
            <div class="onboarding-modal__message">
              Thanks for making changes to your profile, however this has been rejected by a member of the Meshh team.<br /><br />
              Please review the changes and make any necessary corrections before you can make any new bookings.
            </div>

            <div class="onboarding-modal__controls">
              <Button severity="primary" @click="proceed(`/admin/profile/${userStore.userData?.account_id}`)">Proceed</Button>
            </div>
          </template>
          <template v-else>
            <div class="onboarding-modal__message">
              A member of your team has requested changes to your profile, however this has been rejected by a member of the Meshh team.<br /><br />
              These changes will need to be made by a member of your team before you can make any new bookings.
            </div>

            <div class="onboarding-modal__controls">
              <Button severity="primary" @click="proceed(`/`)">Return to Dashboard</Button>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="onboarding-modal__header">Onboarding Pending Approval</div>
          <div class="onboarding-modal__message">
            Thanks for making changes to your profile, this is now pending approval by a member of the Meshh team.
            <br /><br />We will email you once this has been approved and then you will be able to proceed with making
            new bookings and creating events.
          </div>

          <div class="onboarding-modal__controls">
            <Button severity="primary" @click="proceed(`/users`)">Add Users</Button>
          </div>
        </template>
      </div>
    </div>
  </DialogBase>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import Button from './Button.vue';
import DialogBase from '../dialogs/DialogBase.vue';

import { ProfileStatusEnum } from '@/types/User';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

const router = useRouter();
const route = useRoute();

const visible = ref<boolean>(false);

const profileStatus = computed(() => userStore.userData?.profile_status);

watch(route, (newVal) => {
  const { path } = newVal;
  let validPath = true;
  visible.value = false;
  if (profileStatus.value === ProfileStatusEnum.PENDING_REAPPROVAL || profileStatus.value === ProfileStatusEnum.CHANGES_REJECTED) {
    const invalidPaths = ['booking'];
    validPath = invalidPaths.some((p) => path.includes(p));
  } else {
    const validPaths = ['profile', 'login', 'logout', 'users', 'faq', 'api', 'contact'];
    validPath = validPaths.every((p) => !path.includes(p));
  }
  if (validPath && userStore.userData) {
    if (userStore.isNonStaff && !userStore.isOperationsUser && userStore.userData.profile_status && userStore.userData.profile_status !== ProfileStatusEnum.COMPLETE) {
      visible.value = true;
    } else {
      visible.value = false;
    }
  }
});

const proceed = (link: string) => {
  visible.value = false;
  router.push(link);
};
</script>

<style lang="scss">
.onboarding-modal {
  text-align: center;

  .onboarding-modal__image {
    margin-bottom: variables.$sizing-lg;
    width: 260px;
  }

  .onboarding-modal__header {
    @include typography.font-display($size: sm, $weight: bold);
    color: variables.$primitives-color-tertiary-600;
  }

  .onboarding-modal__message {
    @include sizing.margin('xs', 'auto');
    @include typography.font-body($size: md, $weight: normal);
    line-height: 20px;
    width: 400px;
  }

  .onboarding-modal__controls {
    @include mixins.flex-center($gap: variables.$sizing-lg);
    margin-top: variables.$sizing-lg;
  }
}
</style>
